import { assertNotEmpty, type Optional } from '@festina-life/core'
import {
  FPlusCodeContainer,
  pixelsToFPlusCharacters,
} from '@festina-life/fplus'
import { useCallback, useEffect, useId, useRef, useState } from 'react'

import { useConfigurationContext } from '../../context/configurationContext'
import type { FPlusExpressionValue } from '../../types/templateModel'
import { useFormatFPlusExpression } from '../../uiModels/api/formatter/useFormatFPlusExpression'
import { FPlusEditorModal } from '../fplusEditor/FPlusEditorModal'
import { FPlusInlineEditor } from '../fplusEditor/FPlusInlineEditor'
import { type EditMode, HoverEditGroup } from '../hoverEditGroup/HoverEditGroup'
import { FPlusCustomExpressionEditor } from './FPlusCustomExpressionEditor'
import { FPlusViewerContents } from './FPlusViewerContents'
import type { FPlusExpressionProps } from './types'

type Props = FPlusExpressionProps & {
  onChange: (nextValue: FPlusExpressionValue) => Promise<void>
  fallbackCode: Optional<string>
  label?: string
  /**
   * Constructor editors allow us to nest editors inside simple editors.
   * A nested editor does not need to acquaint for the ActiveFPlusEditorId value.
   * */
  isNested?: boolean
  /**
   * For constructor editors that belong to declared or dynamic functions and that therefore depend on the function context,
   * we need to inject a simulated function into the language service in order for it to correctly validate the user input.
   *
   * For example if we have the following function:
   *
   * someFunction(cover: Cover, calculate: (c: Cover) => Integer): SomeReturnType
   *
   * Any editor mounted for a TransformPostingUnTemplated parameter will need to accept references to the `cover` and `calculate` parameters.
   * Therefore, each parameter will need to be scoped by a function of the following form:
   *
   * someRandomStringName(...allParameters_in_the_parent_function): ReturnType_of_the_parameter
   *
   * So for the example above, if SomeReturnType has a parameter `amount` of type Integer, the simulated function declaration could be:
   *
   * kgjdflgjdfkgd(cover: Cover, calculate: (c: Cover) => Integer): Integer
   *
   * This allows the language service to correctly validate the user input and it's important that it's discarded after a parameter is edited.
   */
  simulatedFunctionDeclaration?: Optional<string>
}

export const FPlusExpressionEditor = ({
  path,
  expression,
  isTransparent,
  onChange,
  fallbackCode,
  label,
  typeInformation,
  shouldShowOverflowFallback,
  type,
  isNested,
  simulatedFunctionDeclaration,
}: Props) => {
  const [editMode, setEditMode] = useState<EditMode>()
  const fplusEditorId = useId()
  const { activeFPlusEditorId, setActiveFPlusEditorId } =
    useConfigurationContext()
  const containerRef = useRef<HTMLDivElement>(null)

  const code = expression?.fplusSource ?? fallbackCode ?? ''
  const { formattedCode, ref } = useFormatFPlusExpression(code)
  const { formattedCode: fullScreenFormattedCode } = useFormatFPlusExpression(
    code,
    // Full screen modals are 100vw - 100px wide so therefore the formatted should be narrower than the editor
    pixelsToFPlusCharacters(window.innerWidth - 200),
  )

  useEffect(
    () =>
      function resetEditorIfActiveOnUnmount() {
        setActiveFPlusEditorId((currentId) =>
          currentId === fplusEditorId ? undefined : currentId,
        )
      },
    [fplusEditorId, setActiveFPlusEditorId],
  )

  const handleEdit = (mode: EditMode) => {
    setActiveFPlusEditorId(fplusEditorId)
    setEditMode(mode)
  }

  const handleChangeCode: typeof onChange = useCallback(
    async (nextExpression: FPlusExpressionValue) => {
      assertNotEmpty(onChange)
      try {
        await onChange(nextExpression)

        setEditMode(undefined)
        setActiveFPlusEditorId(undefined)
      } catch {
        // Nothing to do here, the parent component must handle the exception with some fallback UI
      }
    },
    [onChange, setEditMode, setActiveFPlusEditorId],
  )

  const handleCancel = useCallback(() => {
    setEditMode(undefined)
    setActiveFPlusEditorId(undefined)
  }, [setEditMode, setActiveFPlusEditorId])

  return (
    <div className="size-full" ref={containerRef}>
      {editMode === 'modal' && (
        <FPlusEditorModal
          originalCode={code}
          code={fullScreenFormattedCode ?? ''}
          expressionPath={path}
          onChangeCode={handleChangeCode}
          onClose={handleCancel}
          type={type}
          simulatedFunctionDeclaration={simulatedFunctionDeclaration}
        />
      )}
      {editMode === 'inline' && (
        <FPlusInlineEditor
          isTransparent={isTransparent}
          code={formattedCode || fallbackCode || ''}
          expressionPath={path}
          onCancel={handleCancel}
          onChangeCode={handleChangeCode}
          initialHeightPx={ref.current?.getBoundingClientRect().height ?? 0}
          type={type}
          simulatedFunctionDeclaration={simulatedFunctionDeclaration}
        />
      )}
      {editMode === 'custom' && (
        <FPlusCustomExpressionEditor
          expressionPath={path}
          typeInformation={typeInformation!}
          onCancel={handleCancel}
          onChangeCode={handleChangeCode}
          initialHeightPx={ref.current?.getBoundingClientRect().height ?? 0}
          isTransparent={isTransparent}
        />
      )}
      {!editMode && (
        <FPlusCodeContainer
          className="relative"
          isTransparent={isTransparent}
          label={label}
          ref={ref}
        >
          <FPlusViewerContents
            expression={expression}
            path={path}
            alignment="left"
            isShowingFunctionDeclaration={false}
            typeInformation={typeInformation}
            shouldShowOverflowFallback={shouldShowOverflowFallback}
          />
          <HoverEditGroup
            disabled={
              !isNested &&
              !!activeFPlusEditorId &&
              activeFPlusEditorId !== fplusEditorId
            }
            onEdit={handleEdit}
            isSimpleEditingAvailable={!!typeInformation?.value}
          />
        </FPlusCodeContainer>
      )}
    </div>
  )
}
